import { useState } from "react";
import { BasicDialog } from "./PhoneVerificationBasicDialog";
import { Input } from "@/shared/widgets/input";
import { useIsMobile } from "@/core/hooks/useMediaQuery";
import { Loader2 } from "lucide-react";
import type { VerifyPhoneStep } from "../hooks/useVerifyPhoneNumber";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: VerifyPhoneStep;
  phoneNumber: string;
  handlePhoneChange: (val: string) => void;
  isLoading: boolean;
  errorMessage: string;
  successMessage: string;
  sendOtp: () => void;
  resendOtp: () => void;
  verifyOtp: (otp: string) => void;
  goBackToPhone: () => void;
  handleLogout: () => void;
}

export function VerifyPhoneNumberDialog({
  open,
  step,
  phoneNumber,
  handlePhoneChange,
  isLoading,
  errorMessage,
  successMessage,
  sendOtp,
  resendOtp,
  verifyOtp,
  goBackToPhone,
  handleLogout
}: Props) {
  const isMobile = useIsMobile();
  const [otp, setOtp] = useState("");

  const handleVerify = () => {
    verifyOtp(otp);
  };

  return (
    <BasicDialog open={open}>
      <div className={`flex flex-col`}>
        {step === "PHONE" && (
          <>
            <p className="text-xl font-semibold mb-2">
              Secure your account with a mobile number
            </p>
            <p className="text-sm text-gray-600 mb-3">
              We will use this to secure your account and send delivery & support updates
            </p>

            <div className="flex flex-col gap-1">
              <div className="flex flex-col items-center gap-2">
                <div className="relative flex items-center border border-gray-300 overflow-hidden bg-white w-full mb-1">
                  <span className={`text-sm text-gray-500 ${isMobile ? "px-1 pl-2 py-3" : "py-3 px-3"}`}>
                    +91
                  </span>
                  <Input
                    type="tel"
                    placeholder="Enter mobile number"
                    value={phoneNumber}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    disabled={isLoading}
                    className="border-0 focus-visible:ring-0 h-9 rounded-none bg-transparent"
                  />
                  {errorMessage && (
                    <span className="absolute right-0 px-3 bg-white text-xs text-red-500 font-medium whitespace-nowrap pointer-events-none">
                      {errorMessage}
                    </span>
                  )}
                </div>
                <div className={`flex gap-2 w-full ${isMobile ? "flex-col flex-col-reverse" : "flex-row"}`}>
                  <button onClick={handleLogout} className={`${isMobile ? "text-[#4a90e2]":"border border-gray p-3"}`}>
                    Logout
                  </button>

                  <button
                    type="button"
                    disabled={phoneNumber.length !== 10 || isLoading}
                    onClick={sendOtp}
                    className={`border border-hover flex-1 h-full w-full text-md font-medium text-white flex items-center justify-center transition-colors bg-hover
                      ${isMobile ? "py-3 px-4" : "px-[40px] py-3"}
                      disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isLoading ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {step === "OTP" && (
          <>
            <p className="text-xl font-semibold mb-2">
              Verify your number +91-{phoneNumber}
            </p>
            <p className="text-sm text-gray-600 mb-3">
              We have sent an OTP on your mobile number & email. Please enter the OTP below.
            </p>

            <div className="flex flex-col gap-4">
              <div className="relative w-full h-[50px]">
                <input
                  type="text"
                  maxLength={4}
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => {
                    if (/^\d*$/.test(e.target.value)) setOtp(e.target.value);
                  }}
                  disabled={isLoading}
                  className={`border border-gray-300 px-3 py-2 w-full h-full outline-none focus:border-brand disabled:bg-gray-50 transition-colors
                    ${(errorMessage || successMessage) ? 'pr-[130px]' : ''}`}
                />
                
                {(errorMessage || successMessage) && (
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-right leading-tight max-w-[200px] ${
                    errorMessage ? "text-red-500" : "text-green-600"
                  }`}>
                    {errorMessage || successMessage}
                  </span>
                )}
              </div>

              <div className="flex justify-between text-[#4a90e2]">
                <button 
                  onClick={resendOtp} 
                  disabled={isLoading} 
                  className="text-left text-sm hover:underline disabled:opacity-50 disabled:no-underline"
                  >
                  Resend OTP
                </button>
                {/* <button onClick={handleLogout}>
                  Logout
                </button> */}
              </div>
              <div className="flex items-center justify-between mt-2 gap-2">
                <button
                  onClick={goBackToPhone}
                  disabled={isLoading}
                  className="p-3 w-[100px] h-full text-md border border-gray-300 text-black font-medium hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>

                <button
                  disabled={otp.length < 4 || isLoading}
                  onClick={handleVerify}
                  className="border border-hover p-3 w-full h-full flex items-center justify-center bg-hover text-md bg-brand text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                  Submit
                </button>
              </div>

            </div>
          </>
        )}
      </div>
    </BasicDialog>
  );
}